/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react';

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {
  Box, createStylesContext, Flex, Spinner, useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ContentAccordion} from '@amblique/king-living/app/components/footer/content-accordion';
import {Showroom} from '@amblique/king-living/app/components/showroom';
import {useFooterBySiteId} from '@amblique/king-living/app/contentful/hooks/use-footer';
import {useIsMobile} from '@amblique/king-living/app/hooks/use-memoized-breakpoint';

const [StylesProvider] = createStylesContext('Footer');

/**
 * Transform Contentful footer columns to the format expected by ContentAccordion
 * @param {Array} columns - Array of FooterColumn items from Contentful
 * @returns {Array} - Array of content objects for ContentAccordion
 */
const transformColumnToAccordionContent = (column) => {
  if (!column) return [];

  // Build HTML links from linksCollection
  const links = column.linksCollection?.items || [];
  const linksHtml = links
    .filter((link) => link?.text) // Only require text, url can be null
    .map((link) => {
      const target = link.openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
      // Use '#' as fallback if url is null
      const href = link.url || '#';

      return `<a href="${href}"${target}>${link.text}</a>`;
    })
    .join('\n');

  return {
    name: column.title || '',
    body: linksHtml,
  };
};

/**
 * Organize footer columns into 3 display columns
 * Based on requirement:
 * - If 5 columns: Col1 gets 1-2, Col2 gets 3-4, Col3 gets 5
 * - Otherwise distribute evenly with last column getting remainder
 * @param {Array} columns - Array of FooterColumn items from Contentful
 * @returns {Object} - Object with footerColumn2, footerColumn3, footerColumn4
 */
const organizeColumns = (columns) => {
  if (!columns || columns.length === 0) {
    return {
      footerColumn2: [],
      footerColumn3: [],
      footerColumn4: [],
    };
  }

  const transformedColumns = columns.map(transformColumnToAccordionContent);

  // For 5 columns: 1-2 in first, 3-4 in second, 5 in third
  if (columns.length === 5) {
    return {
      footerColumn2: transformedColumns.slice(0, 2),
      footerColumn3: transformedColumns.slice(2, 4),
      footerColumn4: transformedColumns.slice(4, 5),
    };
  }

  // For other counts, distribute in groups of 2 (or 1 for remainder)
  if (columns.length <= 3) {
    return {
      footerColumn2: transformedColumns.slice(0, 1),
      footerColumn3: transformedColumns.slice(1, 2),
      footerColumn4: transformedColumns.slice(2, 3),
    };
  }

  if (columns.length === 4) {
    return {
      footerColumn2: transformedColumns.slice(0, 2),
      footerColumn3: transformedColumns.slice(2, 3),
      footerColumn4: transformedColumns.slice(3, 4),
    };
  }

  // For 6+ columns
  return {
    footerColumn2: transformedColumns.slice(0, 2),
    footerColumn3: transformedColumns.slice(2, 4),
    footerColumn4: transformedColumns.slice(4),
  };
};

const Footer = ({...otherProps}) => {
  const styles = useMultiStyleConfig('Footer');
  const isMobile = useIsMobile();
  const {site} = useMultiSite();

  // Fetch footer data from Contentful using siteId
  // Using 'withColumns' variant - custom query with explicit limits to avoid Contentful complexity errors
  const {data: footerData, isLoading: isFooterLoading} = useFooterBySiteId(site?.id, {
    variant: 'withColumns',
  });

  // Organize columns from Contentful data
  const footerContents = useMemo(() => {
    if (!footerData?.columnsCollection?.items?.length) {
      return {
        footerColumn2: [],
        footerColumn3: [],
        footerColumn4: [],
      };
    }

    return organizeColumns(footerData.columnsCollection.items);
  }, [footerData]);

  // Get aboutUs text from Contentful (displayed below the logo)
  const aboutUs = useMemo(() => {
    return footerData?.aboutUs || '';
  }, [footerData]);

  // Get legal text from Contentful (displayed left of payment icons)
  const legalText = useMemo(() => {
    return footerData?.legalText || '';
  }, [footerData]);

  // Get site notes from Contentful (displayed at the very bottom)
  const siteNotes = useMemo(() => {
    return footerData?.siteNotes || '';
  }, [footerData]);

  // Get logo from Contentful or use default
  // Image content type has: desktopAsset.url, mobileAsset.url, tabletAsset.url for image sources
  // The 'url' field on Image is a link URL (not the image source)
  const logoUrl = useMemo(() => {
    // Try desktop asset first, then mobile, then fallback to local
    if (footerData?.logo?.desktopAsset?.url) {
      return footerData.logo.desktopAsset.url;
    }
    if (footerData?.logo?.mobileAsset?.url) {
      return footerData.logo.mobileAsset.url;
    }

    return getAssetUrl('static/img/king-logo.svg');
  }, [footerData]);

  // Get countries from Contentful or use default
  const countries = useMemo(() => {
    if (footerData?.countriesCollection?.items?.length > 0) {
      return footerData.countriesCollection.items;
    }

    // Fallback to default countries
    return null;
  }, [footerData]);

  // Get payment icons from Contentful IconSet or use default
  const paymentIcons = useMemo(() => {
    // The payments field links to IconSet, which contains iconsCollection
    const iconSets = footerData?.paymentsCollection?.items;

    if (iconSets?.length > 0) {
      // Get icons from the first IconSet (or combine all if multiple)
      const allIcons = iconSets.flatMap((iconSet) => iconSet?.iconsCollection?.items || []);

      if (allIcons.length > 0) {
        return allIcons;
      }
    }

    // Fallback to default payment icons
    return null;
  }, [footerData]);

  return (
    <Box as="footer" {...styles.container} {...otherProps}>
      {/* Showroom Section - Full Width Above Footer */}
      <Showroom />
      <VStack {...styles.content} as="section">
        <StylesProvider value={styles}>
          {countries && countries.length > 0 && (
            <Box {...styles.topFooter}>
              {countries.map((country, index) => (
                <Box key={country.sys?.id || index}>
                  {country.url ? (
                    <a
                      href={country.url}
                      rel={country.openInNewTab ? 'noopener noreferrer' : undefined}
                      target={country.openInNewTab ? '_blank' : undefined}
                    >
                      {country.text}
                      {country.totalCount != null && (
                        <sup style={{fontSize: '0.7em', marginLeft: '2px'}}>
                          {country.totalCount}
                        </sup>
                      )}
                    </a>
                  ) : (
                    <>
                      {country.text}
                      {country.totalCount != null && (
                        <sup style={{fontSize: '0.7em', marginLeft: '2px'}}>
                          {country.totalCount}
                        </sup>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
          <Flex {...styles.mainContainer}>
            <Flex {...styles.siteInformation}>
              <Box {...styles.brandSection}>
                <Box className="footer-brand">
                  <img alt="King Living" className="footer-logo" src={logoUrl} />
                  <Box className="footer-info">
                    {aboutUs.split('\n\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Flex>

            {isFooterLoading ? (
              <Box display="flex" justifyContent="center" p={8} width="100%">
                <Spinner size="lg" />
              </Box>
            ) : isMobile ? (
              <Box {...styles.accordionSection}>
                <ContentAccordion content={footerContents.footerColumn2} />
                <ContentAccordion content={footerContents.footerColumn3} />
                <ContentAccordion content={footerContents.footerColumn4} />
              </Box>
            ) : (
              <Flex {...styles.linksSection}>
                <ContentAccordion content={footerContents.footerColumn2} />
                <ContentAccordion content={footerContents.footerColumn3} />
                <ContentAccordion content={footerContents.footerColumn4} />
              </Flex>
            )}
          </Flex>

          <VStack {...styles.bottomFooterWrapper}>
            <Box {...styles.bottomCopyrightWrapper}>
              <Box {...styles.bottomCopyright}>{legalText}</Box>
              {paymentIcons && paymentIcons.length > 0 && (
                <Box {...styles.bottomIcons}>
                  {paymentIcons.map((icon, index) => {
                    const iconTitle = icon?.title || icon?.name || `Payment ${index + 1}`;

                    // Prefer SVG code if available, otherwise use image URL
                    if (icon?.svgCode) {
                      return (
                        <Box
                          key={icon.sys?.id || index}
                          aria-label={iconTitle}
                          dangerouslySetInnerHTML={{__html: icon.svgCode}}
                          title={iconTitle}
                        />
                      );
                    }

                    if (icon?.iconImage?.url) {
                      return (
                        <img
                          key={icon.sys?.id || index}
                          alt={iconTitle}
                          src={icon.iconImage.url}
                        />
                      );
                    }

                    return null;
                  })}
                </Box>
              )}
            </Box>
            <Box {...styles.bottomFooter}>
              {siteNotes}
            </Box>
          </VStack>
        </StylesProvider>
      </VStack>
    </Box>
  );
};

export default Footer;
